import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "0 0 24 24";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M4.414 19.97a.5.5 0 00.707 0l14.85-14.849a.5.5 0 000-.707l-1.061-1.06a.5.5 0 00-.707 0L14.79 6.765a9.435 9.435 0 00-4.492-.267c-1.109.198-2.178.61-3.178 1.122-1.566.799-2.996 1.904-4.243 3.14-.229.229-.46.462-.669.71-.279.332-.279.724 0 1.055.638.756 1.397 1.428 2.169 2.044.485.386.991.755 1.516 1.093l-2.54 2.54a.5.5 0 000 .707l1.061 1.06zm3.932-6.76l4.865-4.865a3.848 3.848 0 00-4.865 4.865zm1.693 4.238c-.28-.06-.557-.131-.83-.213l1.58-1.581a3.847 3.847 0 004.864-4.864l2.45-2.45c.523.337 1.026.704 1.509 1.089.772.615 1.531 1.288 2.169 2.044a.75.75 0 01.21.41v.004l.002.01.002.01.001.019.001.012a.075.075 0 00.002.017l.001.008.001-.003v.074a.07.07 0 01-.002.013l-.001.012v.013a.047.047 0 00-.003.017.082.082 0 00-.003.02l-.001.004a.75.75 0 01-.21.412c-.208.249-.438.482-.669.71-1.246 1.238-2.677 2.34-4.242 3.14-1 .513-2.07.924-3.179 1.122a9.41 9.41 0 01-3.652-.05z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Private"
});
